<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import { useMapGetter } from 'dashboard/composables/store';
import DashboardIframe from './DashboardIframe.vue';

const props = defineProps({
  config: {
    type: Array,
    default: () => [],
  },
  currentChat: {
    type: Object,
    default: () => ({}),
  },
  isVisible: {
    type: Boolean,
    default: false,
  },
  position: {
    type: Number,
    required: true,
  },
});

const FETCH_INFO_MESSAGE = 'chatwoot-dashboard-app:fetch-info';
const APP_CONTEXT_EVENT = 'appContext';
const DARK_THEME = 'dark';
const LIGHT_THEME = 'light';

const getCurrentTheme = () =>
  document.body.classList.contains(DARK_THEME) ? DARK_THEME : LIGHT_THEME;

const { t } = useI18n();
const getContact = useMapGetter('contacts/getContact');
const getCurrentUser = useMapGetter('getCurrentUser');
const customAttributes = useMapGetter('attributes/getAttributes');

const hasOpenedAtleastOnce = ref(false);
const iframeLoading = ref(true);
const currentTheme = ref(getCurrentTheme());
let themeObserver = null;

const contactId = computed(() => props.currentChat?.meta?.sender?.id);

const currentAgent = computed(() => {
  const { id, name, email } = getCurrentUser.value;
  return { id, name, email };
});

const dashboardAppContext = computed(() => {
  return {
    conversation: props.currentChat,
    contact: getContact.value(contactId.value),
    currentAgent: currentAgent.value,
    customAttributes: customAttributes.value,
    theme: currentTheme.value,
  };
});

const getFrameId = index => {
  return `dashboard-app--frame-${props.position}-${index}`;
};

const sendContext = index => {
  // A possible alternative is to use ref instead of document.getElementById
  // However, when ref is used together with v-for, the ref you get will be
  // an array containing the child components mirroring the data source.
  const frameElement = document.getElementById(getFrameId(index));
  if (!frameElement) return;
  const eventData = {
    event: APP_CONTEXT_EVENT,
    data: dashboardAppContext.value,
  };
  frameElement.contentWindow.postMessage(JSON.stringify(eventData), '*');
  iframeLoading.value = false;
};

const sendContextToFrames = () => {
  if (!props.isVisible || iframeLoading.value) return;
  props.config.forEach((_, index) => sendContext(index));
};

watch(
  () => props.isVisible,
  isVisible => {
    if (isVisible) {
      const hasOpened = hasOpenedAtleastOnce.value;
      hasOpenedAtleastOnce.value = true;
      if (hasOpened) sendContextToFrames();
    }
  }
);

watch(customAttributes, () => {
  sendContextToFrames();
});

const triggerEvent = event => {
  if (!props.isVisible) return;
  if (event.data !== FETCH_INFO_MESSAGE) return;

  const frameIndex = props.config.findIndex((_, index) => {
    const frameElement = document.getElementById(getFrameId(index));
    return frameElement?.contentWindow === event.source;
  });
  if (frameIndex >= 0) sendContext(frameIndex);
};

const onThemeChange = () => {
  const theme = getCurrentTheme();
  if (theme === currentTheme.value) return;

  currentTheme.value = theme;
  sendContextToFrames();
};

onMounted(() => {
  window.addEventListener('message', triggerEvent);
  themeObserver = new MutationObserver(onThemeChange);
  themeObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
});

onUnmounted(() => {
  window.removeEventListener('message', triggerEvent);
  themeObserver?.disconnect();
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div v-if="hasOpenedAtleastOnce" class="dashboard-app--container">
    <div
      v-for="(configItem, index) in config"
      :key="index"
      class="dashboard-app--list"
    >
      <LoadingState
        v-if="iframeLoading"
        :message="t('DASHBOARD_APPS.LOADING_MESSAGE')"
        class="dashboard-app_loading-container"
      />
      <DashboardIframe
        v-if="configItem.type === 'frame' && configItem.url"
        :id="getFrameId(index)"
        :src="configItem.url"
        :dashboard-app-context="dashboardAppContext"
        @load="() => sendContext(index)"
      />
    </div>
  </div>
</template>

<style scoped>
.dashboard-app--container,
.dashboard-app--list,
.dashboard-app--list iframe {
  height: 100%;
  width: 100%;
}

.dashboard-app_loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}
</style>
